import React, { PureComponent } from 'react';
import StepCard from '../../../components/StepCard';
import { Banner } from 'Common/components/Molecules/Banner';
import { Button } from 'Common/components/Atoms/Button';
import { CardHeader } from 'Common/components/Atoms/CardHeader';
import { ContentLoader } from 'Common/components/Atoms/ContentLoader';
import { Flex } from 'Common/components/Atoms/Flex';
import { Link } from 'Common/components/Atoms/Link';
import { Loading } from 'Common/components/Atoms/Loading';
import { Text } from 'Common/components/Atoms/Text';
import { Theme } from '@mui/material/styles';
import withTheme from '@mui/styles/withTheme';
import { actions as accountActions } from '../../../reducers/Account';
import { actions as signupsActions } from '../../../reducers/signups';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { faLock } from '@fortawesome/pro-solid-svg-icons';
import slackLogo from '../../../resources/slack-logo.svg';
import { User } from '../../../models/User';
import { logEvent, REVIEWER_ONBOARDING_SLACK } from 'utils/analytics';
import ms from 'ms';
import { Email } from 'constants/email';
import { mail } from 'fluent-mailto';
import {
  accountUserSelector,
  isUpdatingAccountSelector,
} from '../../../selectors/Account';

const ACCOUNT_POLLING_INTERVAL = ms('10s');
const MAX_POLL_ATTEMPTS = 100;

export interface Props {
  user: User;
  fetchAccount: () => void;
  getReviewerSlackUrl: (callback: (url: string, error: string) => void) => void;
  handleSubmit: () => void;
  isUpdating: boolean;
  theme: Theme;
}

interface State {
  error?: React.ReactNode;
  slackUrl?: string;
  slackError?: string;
}

export class SlackConnect extends PureComponent<Props, State> {
  state: State = {
    error: undefined,
    slackUrl: undefined,
    slackError: undefined,
  };

  timer?: NodeJS.Timeout = undefined;
  attempts = 0;

  componentDidMount() {
    const { getReviewerSlackUrl } = this.props;
    getReviewerSlackUrl((slackUrl, error) => {
      this.setState({
        slackUrl,
        error: error ? (
          <React.Fragment>
            There was an error retrieving the Reviewer Slack sign in URL. Please
            contact us at{' '}
            <Link href={mail.to(Email.SUPPORT).build()}>{Email.SUPPORT}</Link>.
          </React.Fragment>
        ) : null,
      });
    });

    logEvent(REVIEWER_ONBOARDING_SLACK);
  }

  componentWillUnmount() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = undefined;
    }
  }

  startTimer = () => {
    const { fetchAccount, user } = this.props;
    if (!this.timer) {
      fetchAccount();
      this.timer = setInterval(() => {
        this.attempts++;
        fetchAccount();
      }, ACCOUNT_POLLING_INTERVAL);
    } else if (
      this.timer &&
      (this.attempts > MAX_POLL_ATTEMPTS || user.reviewer_profile?.slack_id)
    ) {
      clearInterval(this.timer);
      this.timer = undefined;
    }
  };

  handleSubmit = () => {
    const { handleSubmit, user } = this.props;
    if (user.reviewer_profile?.slack_id) {
      handleSubmit();
    } else {
      this.setState({
        error:
          'You must connect to the PullRequest Reviewer Slack workspace to proceed.',
      });
    }
  };

  render() {
    const { isUpdating, theme, user } = this.props;
    const { slackUrl, error } = this.state;
    return (
      <StepCard
        handleSubmit={this.handleSubmit}
        showPreviousButton={false}
        isUpdating={isUpdating}
      >
        <CardHeader>Join our Slack Workspace</CardHeader>
        <Text variant="body1" mb={2}>
          Join the PullRequest Reviewer Slack workspace by clicking the button
          below.
        </Text>
        <Text variant="body1" mb={2}>
          <b>NOTE:</b> Make sure to sign in with the email associated to your
          PullRequest account: {user.email}
        </Text>
        {!user.reviewer_profile?.slack_id ? (
          <React.Fragment>
            {slackUrl ? (
              <Flex alignItems="center">
                <Button
                  data-testid="qa-slack-connect"
                  variant="contained"
                  href={slackUrl}
                  target="_blank"
                  customcolor={theme.palette.background.paper}
                  onClick={this.startTimer}
                  height={48}
                  width={180}
                >
                  <Flex justifyContent="center" alignItems="center">
                    <img
                      src={slackLogo}
                      height="15px"
                      width="15px"
                      alt="Sign in with Slack"
                    />
                    <Text display="inline" variant="h4" ml={1}>
                      Join Us On Slack
                    </Text>
                  </Flex>
                </Button>
                {this.timer && (
                  <Flex ml={3}>
                    <Loading size={24} />
                    <Text variant="body1" ml={3} color="textSecondary">
                      Waiting for Slack connection...
                    </Text>
                  </Flex>
                )}
              </Flex>
            ) : (
              <ContentLoader width="180px" height="48px" />
            )}
            {error && (
              <Text variant="caption" color="error" mt={3}>
                {error}
              </Text>
            )}
          </React.Fragment>
        ) : (
          <Banner color={theme.palette.success.main} icon={faLock} mt={6}>
            <Text variant="body1" ml={2}>
              <b>Connected</b> to the PullRequest Reviewer Slack workspace with
              email <b>{user.email}</b>.
            </Text>
          </Banner>
        )}
      </StepCard>
    );
  }
}

function mapStateToProps(state: any) {
  const user = accountUserSelector(state);
  const isUpdating = isUpdatingAccountSelector(state);

  return {
    user,
    isUpdating,
  };
}

function mapDispatchToProps(dispatch: Dispatch<{}>) {
  return bindActionCreators({ ...accountActions, ...signupsActions }, dispatch);
}

export default withTheme(
  connect(mapStateToProps, mapDispatchToProps)(SlackConnect)
);
